/* istanbul ignore file */
import 'locales/i18n';
import './assets/styles/global';
import { Helmet } from 'react-helmet-async';

import { ReactElement } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import * as Sentry from '@sentry/react';

import { Toaster } from 'react-hot-toast';
import dayjs from 'dayjs';
import isToday from 'dayjs/plugin/isToday';

import ScrollToTop from './ScrollToTop';

import NotificationsPage from 'modules/notifications/pages/NotificationsPage';
import ClassroomListPage from 'modules/classroom/pages/ClassroomListPage';
import ClassroomRegistrationPage from 'modules/classroom/pages/ClassroomRegistrationPage';
import CreateClassroomPage from 'modules/classroom/pages/CreateClassroomPage';
import SignUpAdmin from 'modules/classroom/pages/SignUpAdminPage';
import ZoomIntegrationPageProxy from 'modules/classroom/pages/ZoomIntegrationPageProxy';
import WriteAssignmentPage from 'modules/assignment/pages/WriteAssignmentPage';
import AssignmentReportPage from 'modules/assignment/pages/AssignmentReportPage';

import UnknownErrorPage from 'errors/pages/UnknownErrorPage';
import NotFoundErrorPage from 'errors/pages/NotFoundErrorPage';
import AuthenticationPage from 'modules/account/pages/AuthenticationPage';
import EditAssignmentPage from 'modules/assignment/pages/EditAssignmentPage';
import UpdateSchoolInfoPage from 'modules/account/pages/UpdateSchoolInfoPage';
import LearningCardViewerPage from 'modules/assignment/pages/LearningCardViewerPage';
import CreatePostPage from 'modules/post/pages/CreatePostPage';
import Classroom from 'modules/classroom';
import PrivateRoute from 'components/PrivateRoute';
import CreateAnnouncementPage from 'modules/post/pages/CreateAnnouncementPage';
import Seo from 'components/Seo';
import { env } from 'utils/env';

dayjs.extend(isToday);

const App = (): ReactElement => {
  return (
    <Sentry.ErrorBoundary fallback={() => <UnknownErrorPage />}>
      <Seo />
      <Helmet>
        <body className="body--gray" />
      </Helmet>
      <ScrollToTop />
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            margin: '4.375rem',
          },
        }}
      />
      <Switch>
        <Route
          exact
          path="/"
          render={() => {
            window.location.replace(
              env.STAGE === 'prod'
                ? 'https://www.classting.com'
                : 'https://www.classting.net'
            );

            return null;
          }}
        />
        <Route exact path="/authentication" component={AuthenticationPage} />
        <PrivateRoute path="/classrooms/new" component={CreateClassroomPage} />
        <PrivateRoute
          path="/classrooms/registration"
          component={ClassroomRegistrationPage}
        />
        <PrivateRoute exact path="/classrooms" component={ClassroomListPage} />
        {/* TODO: classroomPage 의 Container를 component 로 이동하면 classroomPage로 이동 */}
        <PrivateRoute
          exact
          path="/classrooms/:classroomId/sign-up/admin"
          component={SignUpAdmin}
        />
        <PrivateRoute
          path={'/classrooms/:classroomId/posts/new'}
          component={CreatePostPage}
        />
        <PrivateRoute
          path="/classrooms/:classroomId/channels/:channelId/announcement-posts/new"
          component={CreateAnnouncementPage}
        />
        <PrivateRoute
          path={'/classrooms/:classroomId/assignments/:assignmentId/new'}
          component={WriteAssignmentPage}
        />
        <PrivateRoute
          exact
          path="/classrooms/:classroomId/assignments/report"
          component={AssignmentReportPage}
        />

        <PrivateRoute
          exact
          path={`/classrooms/:classroomId/posts/:postId/learning-viewer`}
          component={LearningCardViewerPage}
        />

        <PrivateRoute
          path={'/classrooms/:classroomId/assignments/:assignmentId/edit'}
          component={EditAssignmentPage}
        />
        <PrivateRoute
          path="/classrooms/:classroomId/account-school/edit"
          component={UpdateSchoolInfoPage}
        />
        <PrivateRoute path="/classrooms/:classroomId" component={Classroom} />
        <PrivateRoute
          path="/oauth_callback_zoom/classrooms/:classroomId/integrations/zoom"
          component={ZoomIntegrationPageProxy}
        />
        <PrivateRoute path="/notifications" component={NotificationsPage} />

        <Redirect from="/compare" to="/classrooms/new" />
        <Route path="*" component={NotFoundErrorPage} />
      </Switch>
    </Sentry.ErrorBoundary>
  );
};

export default App;
